import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rgApi } from "@/lib/rgApi";
import { DEFAULT_CLEANING_PROTOCOL } from "./rgCleaningProtocol";

/**
 * CleaningStatsBlock — Réglages : cibles (temps cible) et records (faux records) par bloc.
 * - Temps cible éditable en minutes → apparaît dans le Cockpit « Cible ≈ XX min ».
 * - Record visible/modifiable + réinitialisation (avec confirmation).
 * N'altère ni les durées enregistrées, ni les dernières durées, ni l'historique.
 */

function minFromSec(sec) { return sec ? Math.round(sec / 60) : 0; }

// Libellés lisibles (fallback sur la clé)
function blocLabels() {
  const map = {};
  (DEFAULT_CLEANING_PROTOCOL.blocs || []).forEach((b) => { map[b.key] = b.title; });
  map.decor = "🌹 Déco du Refuge";
  map.bain_fleurs = "🌸 Bain de fleurs";
  map.child_bed = "🛏️ Couchage enfant";
  return map;
}

export default function CleaningStatsBlock() {
  const [stats, setStats] = useState({});
  const [targetDraft, setTargetDraft] = useState({});
  const [recordDraft, setRecordDraft] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const labels = blocLabels();

  const load = () => rgApi.cleaningStats()
    .then((d) => {
      const b = d.blocs || {};
      setStats(b);
      const td = {}; const rd = {};
      Object.entries(b).forEach(([k, v]) => {
        td[k] = String(minFromSec(v.target_sec));
        rd[k] = v.best?.duration_sec ? String(minFromSec(v.best.duration_sec)) : "";
      });
      setTargetDraft(td); setRecordDraft(rd);
    })
    .catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveTarget = async (k) => {
    const min = parseInt(targetDraft[k], 10);
    if (isNaN(min) || min < 0) { toast.error("Cible invalide"); return; }
    setBusyKey(k);
    try {
      await rgApi.updateCleaningTarget(k, min * 60);
      toast.success(`Cible mise à jour : ≈ ${min} min`);
      await load();
    } catch (e) { toast.error("Échec", { description: e?.message }); }
    finally { setBusyKey(""); }
  };

  const saveRecord = async (k) => {
    const min = parseInt(recordDraft[k], 10);
    if (isNaN(min) || min <= 0) { toast.error("Record invalide (minutes > 0)"); return; }
    setBusyKey(k);
    try {
      await rgApi.updateCleaningRecord(k, min * 60);
      toast.success(`Record fixé : ${min} min`);
      await load();
    } catch (e) { toast.error("Échec", { description: e?.message }); }
    finally { setBusyKey(""); }
  };

  const resetRecord = async (k) => {
    if (!window.confirm(`Réinitialiser le record du bloc « ${labels[k] || k} » ?\n\nCela supprime le record actuel (faux record). Les durées enregistrées et l'historique ne sont pas modifiés.`)) return;
    setBusyKey(k);
    try {
      await rgApi.updateCleaningRecord(k, null);
      toast.success("Record réinitialisé");
      await load();
    } catch (e) { toast.error("Échec", { description: e?.message }); }
    finally { setBusyKey(""); }
  };

  const keys = Object.keys(stats);

  return (
    <div className="rg-card" style={{ marginBottom: 16 }} data-testid="rg-cleaning-stats-block">
      <h3 className="rg-h3">🏆 Temps cibles & records du Cockpit ménage</h3>
      <p className="rg-muted" style={{ margin: "4px 0 10px", fontSize: 12 }}>
        Ajuste le temps cible (en minutes) de chaque bloc — il apparaît dans le Cockpit sous « Cible ≈ XX min ».
        Corrige ou réinitialise un faux record. Les durées déjà enregistrées, les dernières durées et l&apos;historique ne sont jamais modifiés.
      </p>
      {keys.length === 0 && <p className="rg-muted">Chargement…</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {keys.map((k) => {
          const v = stats[k] || {};
          const recordMin = v.best?.duration_sec ? minFromSec(v.best.duration_sec) : null;
          const lastMin = v.last?.duration_sec ? minFromSec(v.last.duration_sec) : null;
          return (
            <div key={k} className="rg-card" style={{ padding: 12, background: "var(--rg-bg)" }} data-testid={`rg-stats-bloc-${k}`}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{labels[k] || k}</div>
              <div className="rg-row" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div>
                  <label className="rg-tiny" style={{ display: "block", color: "var(--rg-text-3)", marginBottom: 2 }}>Temps cible (min)</label>
                  <div className="rg-row" style={{ gap: 6 }}>
                    <input type="number" min={0} className="rg-input" style={{ width: 80 }}
                      value={targetDraft[k] ?? ""} onChange={(e) => setTargetDraft((d) => ({ ...d, [k]: e.target.value }))}
                      data-testid={`rg-stats-target-${k}`} />
                    <button className="rg-btn small" disabled={busyKey === k} onClick={() => saveTarget(k)} data-testid={`rg-stats-target-save-${k}`}>OK</button>
                  </div>
                </div>
                <div>
                  <label className="rg-tiny" style={{ display: "block", color: "var(--rg-text-3)", marginBottom: 2 }}>
                    Record (min){recordMin ? ` · actuel 🏆 ${recordMin}` : " · aucun"}
                  </label>
                  <div className="rg-row" style={{ gap: 6 }}>
                    <input type="number" min={1} className="rg-input" style={{ width: 80 }} placeholder="—"
                      value={recordDraft[k] ?? ""} onChange={(e) => setRecordDraft((d) => ({ ...d, [k]: e.target.value }))}
                      data-testid={`rg-stats-record-${k}`} />
                    <button className="rg-btn small" disabled={busyKey === k} onClick={() => saveRecord(k)} data-testid={`rg-stats-record-save-${k}`}>OK</button>
                    <button className="rg-btn ghost small danger" disabled={busyKey === k || !recordMin} onClick={() => resetRecord(k)} data-testid={`rg-stats-record-reset-${k}`}>Réinitialiser</button>
                  </div>
                </div>
                {lastMin != null && <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>Dernière : {lastMin} min</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
